import { useState } from 'react'
import { Download, FileJson, FileText } from 'lucide-react'
import {
  defaultFilename,
  defaultFilenameText,
  downloadAsFile,
  serializeAsText,
  serializeExport,
} from '@/core/io/exporter'
import {
  useEntriesStore,
  useRateCacheStore,
  useSettingsStore,
} from '@/core/storage/zustand-store'
import { Button } from '../shadcn/button'
import { Input } from '../shadcn/input'
import { Label } from '../shadcn/label'
import { Switch } from '../shadcn/switch'
import { Modal } from './Modal'

export interface ExportDialogProps {
  open: boolean
  defaultAnoBase: number
  onClose: () => void
}

export function ExportDialog({ open, defaultAnoBase, onClose }: ExportDialogProps) {
  const entries = useEntriesStore((s) => s.entries)
  const rates = useRateCacheStore((s) => s.rates)
  const settings = useSettingsStore((s) => s.settings)

  const [format, setFormat] = useState<'json' | 'text'>('json')
  const [scope, setScope] = useState<'year' | 'all'>('year')
  const [includeCache, setIncludeCache] = useState(false)
  const [withPassword, setWithPassword] = useState(false)
  const [password, setPassword] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleExport = async (): Promise<void> => {
    setPending(true)
    setError(null)
    try {
      const anoBase: number | 'all' = scope === 'all' ? 'all' : defaultAnoBase
      if (format === 'text') {
        const content = serializeAsText({ entries, anoBase })
        downloadAsFile(content, defaultFilenameText(anoBase))
      } else {
        const content = await serializeExport({
          entries,
          anoBase,
          ...(includeCache ? { rateCache: rates } : {}),
          knownCategorias: settings.knownCategorias,
          knownTags: settings.knownTags,
          knownPagadores: settings.knownPagadores,
          ...(withPassword && password ? { password } : {}),
        })
        downloadAsFile(content, defaultFilename(anoBase))
      }
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'falha ao exportar')
    } finally {
      setPending(false)
    }
  }

  const canExport =
    !pending && (format === 'text' || !withPassword || password.length >= 8)

  return (
    <Modal
      open={open}
      title="Exportar dados"
      description="Baixe um JSON do seu ano-base ou de todos os lançamentos."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              void handleExport()
            }}
            disabled={!canExport}
          >
            <Download />
            {pending ? 'Exportando…' : 'Baixar arquivo'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <fieldset className="space-y-2">
          <Label className="text-muted-foreground text-[11px] font-medium tracking-widest2 uppercase">
            Formato
          </Label>
          <div className="flex gap-2">
            <FormatOption
              checked={format === 'json'}
              icon={<FileJson className="h-4 w-4" />}
              label="JSON"
              description="estruturado, criptografável, re-importável"
              onClick={() => setFormat('json')}
            />
            <FormatOption
              checked={format === 'text'}
              icon={<FileText className="h-4 w-4" />}
              label="Texto"
              description="carnê-leão: pagador / BRL / moeda / USD"
              onClick={() => setFormat('text')}
            />
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          <Label className="text-muted-foreground text-[11px] font-medium tracking-widest2 uppercase">
            Escopo
          </Label>
          <div className="flex gap-2">
            <ScopeOption
              checked={scope === 'year'}
              label={`Apenas ${defaultAnoBase}`}
              onClick={() => setScope('year')}
            />
            <ScopeOption
              checked={scope === 'all'}
              label="Todos"
              onClick={() => setScope('all')}
            />
          </div>
        </fieldset>

        {format === 'json' ? (
          <>
        <div className="bg-muted/40 flex items-center justify-between gap-4 rounded-md border border-border p-3">
          <div>
            <Label htmlFor="includeCache" className="text-sm font-medium">
              Incluir cache de cotações
            </Label>
            <p className="text-muted-foreground text-[11px]">
              Acelera reimportação offline.
            </p>
          </div>
          <Switch
            id="includeCache"
            checked={includeCache}
            onCheckedChange={setIncludeCache}
          />
        </div>

        <div className="bg-muted/40 space-y-3 rounded-md border border-border p-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="withPassword" className="text-sm font-medium">
                Criptografar com senha
              </Label>
              <p className="text-muted-foreground text-[11px]">
                AES-GCM-256 · PBKDF2 600k.
              </p>
            </div>
            <Switch
              id="withPassword"
              checked={withPassword}
              onCheckedChange={setWithPassword}
            />
          </div>
          {withPassword ? (
            <div>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="mínimo 8 caracteres"
                aria-label="Senha"
                className="font-mono"
              />
              <p className="text-muted-foreground mt-1 text-[11px]">
                Sem recuperação — guarde a senha em um gerenciador.
              </p>
            </div>
          ) : null}
        </div>
          </>
        ) : (
          <div className="bg-muted/40 rounded-md border border-border p-3 text-[12px]">
            <p className="mb-2 font-medium">Como o arquivo de texto sai:</p>
            <pre className="text-muted-foreground bg-background rounded border border-border p-2 font-mono text-[11px] leading-relaxed select-text">
{`Pagador
BRL 21.873,91
CAD 5.192,31 (24·JAN·25)
USD 3.621,868 (15·DEZ·24)`}
            </pre>
            <p className="text-muted-foreground mt-2 text-[11px]">
              Inclui apenas lançamentos com snapshot calculado. Use o JSON se
              precisar reimportar.
            </p>
          </div>
        )}

        {error ? <p className="text-destructive text-sm">{error}</p> : null}
      </div>
    </Modal>
  )
}

function FormatOption({
  checked,
  icon,
  label,
  description,
  onClick,
}: {
  checked: boolean
  icon: React.ReactNode
  label: string
  description: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="radio"
      aria-checked={checked}
      className={[
        'flex flex-1 flex-col items-start gap-1 rounded-md border p-3 text-left text-sm transition-colors',
        checked
          ? 'border-primary bg-primary/5'
          : 'border-input bg-background hover:bg-muted',
      ].join(' ')}
    >
      <div className="flex items-center gap-2 font-medium">
        {icon}
        {label}
      </div>
      <span className="text-muted-foreground text-[11px]">{description}</span>
    </button>
  )
}

function ScopeOption({
  checked,
  label,
  onClick,
}: {
  checked: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="radio"
      aria-checked={checked}
      className={[
        'flex h-9 flex-1 items-center justify-center rounded-md border text-sm font-medium transition-colors',
        checked
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-input bg-background hover:bg-muted',
      ].join(' ')}
    >
      {label}
    </button>
  )
}
